import { useConvexMutation } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import type { Id } from '@cvx/_generated/dataModel'
import type { JobQuestionNode } from '@cvx/features/jobs/job_questions_db'
import { Input } from '@rekrytering/ui/components/input'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

type Props = {
  readonly jobId: Id<'jobs'>
  readonly questions: readonly JobQuestionNode[]
  readonly competencyWeights: Record<string, number> | undefined
  readonly isLocked: boolean
}

const extractUniqueCompetencies = (
  questions: readonly JobQuestionNode[],
): readonly string[] => {
  const seen = new Set<string>()
  for (const q of questions) {
    if (q.competency) seen.add(q.competency)
    for (const fu of q.followUps) {
      if (fu.competency) seen.add(fu.competency)
    }
  }
  return [...seen].sort()
}

export const CompetencyWeightsEditor = ({
  jobId,
  questions,
  competencyWeights,
  isLocked,
}: Props) => {
  const { t } = useTranslation()
  const { mutateAsync: updateJob, isPending } = useMutation({
    mutationFn: useConvexMutation(api.features.jobs.jobs.updateMyJob),
  })

  const competencies = extractUniqueCompetencies(questions)

  const handleWeightChange = (competency: string, value: string): void => {
    const weight = parseFloat(value)
    if (Number.isNaN(weight) || weight <= 0) return

    const updated = { ...(competencyWeights ?? {}), [competency]: weight }
    updateJob({ jobId, competencyWeights: updated })
  }

  if (competencies.length === 0) {
    return (
      <p className="text-xs text-primary/40">
        {t('jobs.settings.competencyWeightsNoCompetencies')}
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-primary/50">
        {t('jobs.settings.competencyWeightsDescription')}
      </p>

      <div className="flex flex-col gap-2">
        {competencies.map((competency) => (
          <div key={competency} className="flex items-center gap-3">
            <span className="min-w-0 flex-1 truncate text-sm text-primary">
              {competency}
            </span>
            <Input
              type="number"
              min={0.1}
              step={0.1}
              defaultValue={competencyWeights?.[competency] ?? 1}
              onBlur={(e) => handleWeightChange(competency, e.target.value)}
              className="w-24 text-right"
              disabled={isLocked || isPending}
            />
          </div>
        ))}
      </div>

      {isLocked && (
        <p className="text-xs text-primary/40">
          {t('jobs.settings.competencyWeightsLocked')}
        </p>
      )}
    </div>
  )
}
